import type { ColumnState } from 'ag-grid-community';

/**
 * REG-828: "Save as default layout" on a metadata-driven table (TableGrid)
 * writes the table's list layout in core_crm.table_ui_component, the same
 * config Page Builder edits, so the two share one source of truth and every
 * metadata table (including ones created later) gets the feature.
 *
 * The grid sends only the shown columns in order with their widths;
 * public.save_table_list_layout keeps each field's Page Builder settings
 * (label, date format, editability, flex sizing), adds newly shown fields and
 * drops hidden ones (offered hidden). Columns that are not fields of the table
 * (row actions, selection) are left out.
 */
export interface ListLayoutColumn {
  field: string;
  width?: number;
}

/**
 * The table field a grid column belongs to: its own id, or for a calculated
 * field read from the view's `<name>_calc` column, `<name>`.
 */
function layoutField(colId: string, tableFields: ReadonlySet<string>): string | null {
  if (tableFields.has(colId)) return colId;
  const base = colId.endsWith('_calc') ? colId.slice(0, -'_calc'.length) : null;
  return base && tableFields.has(base) ? base : null;
}

export function listColumnsFromGrid(
  state: readonly ColumnState[],
  tableFields: ReadonlySet<string>,
): ListLayoutColumn[] {
  return state.flatMap((column) => {
    const field = column.hide ? null : layoutField(column.colId, tableFields);
    if (!field) return [];
    const entry: ListLayoutColumn = { field };
    if (typeof column.width === 'number' && column.width > 0) {
      entry.width = Math.min(4000, Math.max(10, Math.round(column.width)));
    }
    return [entry];
  });
}
